import { TextSelection } from '@tiptap/pm/state'
import { Extension, Node, wrappingInputRule, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Code from '@tiptap/extension-code';
import Paragraph from '@tiptap/extension-paragraph';
import Link from '@tiptap/extension-link';
import isEmail from 'validator/es/lib/isEmail';

import ImageLinkComponent from './ImageLinkComponent';
import CustomImageComponent from './CustomImageComponent';
import BulletItemComponent from './BulletItemComponent';
import OrderedItemComponent from './OrderedItemComponent'; 
import HorizontalRuleComponent from './HorizontalRuleComponent';




const inputRegex = /^\s*>\s$/;

export const CustomLink = (baseTemplateUrl) => {

  return Link.extend({
    addAttributes() {
      return {
        href: {
          renderHTML: (attributes) => {

            let customUrl = attributes.href;
            let isFullUrl;
            let isEmailThis;

            if (customUrl && customUrl.includes('https://') || customUrl && customUrl.includes('http://')) {
              isFullUrl = true;
            }
        
            if (isEmail(customUrl)) {
              isEmailThis = true;
            }

            if (!isEmailThis && !isFullUrl && customUrl.charAt(0) !== '/') {
              return;
            }
        
            const thisBaseTemplateUrl = baseTemplateUrl ? baseTemplateUrl : '';

            return {
              href: isEmailThis ? 'mailto:'+customUrl : isFullUrl ? customUrl : thisBaseTemplateUrl+customUrl,
              target: isFullUrl ? '_blank' : '_self'
            };
          }
        },
  
      };
    },
    renderHTML(props) {
      const {
        HTMLAttributes
      } = props;

      const newAttributes = HTMLAttributes;

      const aElement = document.createElement(HTMLAttributes.href ? 'a' : 'span');

      if (HTMLAttributes.href) {
        if (newAttributes) {
          for (const [key, value] of Object.entries(newAttributes)) {
            aElement.setAttribute(key, value);
          }
        }
  
        setTimeout(() => {
          if (aElement) {
            const getColor = aElement?.firstChild?.style?.color || aElement?.parentNode?.style?.color || getComputedStyle(aElement, null).color;
            aElement.style.color = getColor;
          }
        });
      }

      return aElement;
    },
  })
}

export const CustomParagraph = Paragraph.extend({
  name: 'paragraph',
  addAttributes() {
    return {
      style: {
        default: null
      },
      fontSize: {
        default: null
      },
      fontSizeUnit: {
        default: null
      }
    }
  },
})

export const CustomCode = Code.extend({
  name: 'customCode',
  addAttributes() {
    return {
      style: {
        default: 'font-size: 1em; color: #fff'
      },
      fontSizeInput: {
        default: '1'
      },
      fontSizeUnit: {
        default: 'em'
      },
      color: {
        default: '#fff'
      }
    }
  },

})

export const CustomHorizontalRule = HorizontalRule.extend({
  name: 'customHorizontalRule',
  addAttributes() {
    return {
      borderColor: {
        default: null,
      },
      borderWidth: {
        default: null
      },
      borderWidthUnit: {
        default: null
      }
    }
  },
  addCommands() {
    return {
      setHorizontalRule:
        (options) => ({ chain }) => {
          return (
            chain()
              .insertContent({ type: this.name, attrs: { borderColor: options.borderColor, borderWidth: options.borderWidth, borderWidthUnit: options.borderWidthUnit } })
              // set cursor after horizontal rule
              .command(({ tr, dispatch }) => {
                if (dispatch) {
                  const { $to } = tr.selection
                  const posAfter = $to.end()
                  if ($to.nodeAfter) {
                    tr.setSelection(TextSelection.create(tr.doc, $to.pos))
                  } else {
                    // add node after horizontal rule if it’s the end of the document
                    const node = $to.parent.type.contentMatch.defaultType?.create()

                    if (node) {
                      tr.insert(posAfter, node)
                      tr.setSelection(TextSelection.create(tr.doc, posAfter))
                    }
                  }

                  tr.scrollIntoView()
                }

                return true
              })
              .run()
          )
        },
    }
  },
  addNodeView(props) {
    return ReactNodeViewRenderer(HorizontalRuleComponent)
  },

})

export const CustomBlockquote = Blockquote.extend({
  name: 'customBlockquote',
  addAttributes() {
    return {
      style: {
        default: null,
      },
      borderColor: {
        default: null
      },
      borderWidth: {
        default: null
      },
      borderWidthUnit: {
        default: null
      }
    }
  },
  addCommands() {
    return {
      setBlockquote: options => ({ commands }) => {
        return commands.wrapIn(this.name, options)
      },
      toggleBlockquote: options => ({ commands }) => {
        return commands.toggleWrap(this.name, options)
      },
      unsetBlockquote: options => ({ commands }) => {
        return commands.lift(this.name, options)
      },
    }
  },
})

export const CustomOrderedList = OrderedList.extend({
  name: 'customOrderedList',
  addOptions() {
    return {
      itemTypeName: 'listItem',
      HTMLAttributes: {},
    }
  },
  group: 'block list',

  content() {
    return `${this.options.itemTypeName}+`
  },

  addCommands() {
    return {
      toggleCustomOrderedList: () => ({ commands }) => {
        return commands.toggleList(this.name, this.options.itemTypeName)
      },
    }
  },
  addAttributes() {
    return {
      ...OrderedList.config.addAttributes(),
      color: {
        default: null,
      },
      fontSize: {
        default: null
      },
      fontSizeUnit: {
        default: null
      }
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(OrderedItemComponent)
  },
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-7': () => this.editor.commands.toggleCustomOrderedList(),
    }
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => ({ start: +match[1] }),
        joinPredicate: (match, node) => node.childCount + node.attrs.start === +match[1],
      }),
    ]
  },
})

export const CustomBulletList = BulletList.extend({
  name: 'customBulletList',
  addOptions() {
    return {
      itemTypeName: 'listItem',
      HTMLAttributes: {},
    }
  },
  group: 'block list',

  content() {
    return `${this.options.itemTypeName}+`
  },

  addCommands() {
    return {
      toggleCustomBulletList: () => ({ commands }) => {
        return commands.toggleList(this.name, this.options.itemTypeName)
      },
    }
  },
  addAttributes() {
    return {
      color: {
        default: null,
      },
      fontSize: {
        default: 1
      },
      fontSizeUnit: {
        default: 'em'
      }
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(BulletItemComponent)
  },
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-8': () => this.editor.commands.toggleBulletList(),
    }
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      })
    ]

  },
})

export const CustomImage = Image.extend({
  name: 'customImage',
  addAttributes() {
    return {
      ...Image.config.addAttributes(),
      width: {
        default: '100%',
        renderHTML: (attributes) => {
          return {
            width: attributes.width
          };
        }
      },
      height: {
        default: '100%',
        renderHTML: (attributes) => {
          return {
            height: attributes.height
          };
        }
      },
      float: {
        default: 'none',
        rendered: false
      },
      aspectRatio: {
        default: null
      },
      style: {
        default: null,
      }
    };
  }, addNodeView() {
    return ReactNodeViewRenderer(CustomImageComponent)
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      })
    ]

  },

})

export const ImageLinkNode = Node.create({
  name: "imageLink",
  group: "block",
  draggable: true,
  priority: 1000,
  content: "customImage",

  renderHTML({ HTMLAttributes }) {
    return ['imageLink', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },
  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },
  addAttributes() {
    return {
      href: {
        default: null,
      },
      target: {
        default: this.options.HTMLAttributes.target,
      },
      class: {
        default: this.options.HTMLAttributes.class,
      },
      editable: {
        default: false
      },
      style: {
        default: null,
      },
      draggable: true,
    }
  },

  addCommands() {
    return {
      setImageLink: options => ({ commands }) => {
        return commands.wrapIn(this.name, options)
      },
      toggleImageLink: () => ({ commands }) => {
        return commands.toggleWrap(this.name)
      },
      unsetImageLink: options => ({ commands }) => {
        return commands.lift(this.name, options)
      },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageLinkComponent)
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      })
    ]

  },
})

export const TextTransform = Extension.create({
  name: 'textTransform',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textTransform: {
            default: null,
            parseHTML: element => element.style.textTransform?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.textTransform) {
                return {}
              }

              return {
                style: `text-transform: ${attributes.textTransform}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setTextTransform: textTransform => ({ chain }) => {
        return chain()
          .setMark('textStyle', { textTransform })
          .run()
      },
      unsetTextTransform: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { textTransform: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },

})

export const TextDecoration = Extension.create({
  name: 'textDecoration',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },


  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textDecoration: {
            default: null,
            parseHTML: element => element.style.textDecoration?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.textDecoration) {
                return {}
              }

              return {
                style: `text-decoration: ${attributes.textDecoration}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setTextDecoration: textDecoration => ({ chain }) => {
        return chain()
          .setMark('textStyle', { textDecoration })
          .run()
      },
      unsetTextDecoration: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { textDecoration: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },

})


export const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },


  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
          fontSizeInput: {
            default: null,
          },
          fontSizeUnit: {
            default: null,
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontSize: (fontSize, fontSizeInput, fontSizeUnit) => ({ chain, editor }) => {
        
        return chain()
          .updateAttributes('textStyle', {fontSizeInput: fontSizeInput, fontSizeUnit: fontSizeUnit})
          .setMark('textStyle', { fontSize })
          .run()
          
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },

})

export const FontWeight = Extension.create({
  name: 'fontWeight',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },


  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontWeight: {
            default: null,
            parseHTML: element => element.style.fontWeight?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontWeight) {
                return {}
              }

              return {
                style: `font-weight: ${attributes.fontWeight}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontWeight: fontWeight => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontWeight })
          .run()
      },
      unsetFontWeight: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontWeight: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },

})